import { useEffect, useState } from 'react';
import { t, Trans } from '@lingui/macro';
import { useNavigate } from 'react-router-dom';
import Step1 from './Step1';
import Step2 from './Step2';
import Step3 from './Step3';

import { getAnswer } from './getAnswer';
import { Helmet } from 'react-helmet-async';
import { useLingui } from '@lingui/react';

export type FormData = {
  questionOne: string;
  questionTwo: string;
  questionThree: string;
}

const Wizard = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [stepCompleted, setStepCompleted] = useState(false);
  const [resultLoading, setResultLoading] = useState(false);
  const [answer, setAnswer] = useState('');
  // create some random state that we"ll use to throw errors
  const [, setState] = useState();

  const { i18n } = useLingui();

  const [formData, setFormData] = useState<FormData>({
    questionOne: '',
    questionTwo: '',
    questionThree: '',
  });


  const handleChange = (data: FormData) => {
    setStepCompleted(false);
    setFormData(data);
    setStepCompleted(true);
  }

  const showInstruction = () => {
    window.open(answer, '_blank');
  }

  useEffect(() => {
    setStepCompleted(false);
  }, []);

  useEffect(() => {
    if (formData.questionOne !== '' && formData.questionTwo !== '' && formData.questionThree !== '') {
      setResultLoading(true);
      setTimeout(async () => {
        try {
          const res = await getAnswer(formData, i18n.locale);
          setAnswer(res);
          setResultLoading(false);
        } catch (e) {
          // trigger state update, with updater function as an argument
          setState(() => {
            // re-throw this error within the updater function
            // it will be triggered during state update
            throw e;
          })
        }
      }, 500);
    }
  }, [formData, formData.questionOne, formData.questionThree, formData.questionTwo, i18n.locale, navigate]);


  const conditionalComponent = () => {
    switch (step) {
      case 1:
        return <Step1 formData={formData} handleChange={handleChange} isValid={setStepCompleted} />;
      case 2:
        return <Step2 formData={formData} handleChange={handleChange} isValid={setStepCompleted} />;
      case 3:
        return <Step3 formData={formData} handleChange={handleChange} isValid={setStepCompleted} />;
      default:
        return <Step1 formData={formData} handleChange={setFormData} />;
    }
  };

  return (
    <>
      <Helmet htmlAttributes={{ lang: i18n.locale }}>
        <title>{t`PTV License Support`}</title>
        <meta name="description" content={t`PTV License Support`} />
        <link rel="alternate" hrefLang="en" href="https://secure.ptvgroup.com/license-wizard/en" />
        <link rel="alternate" hrefLang="de" href="https://secure.ptvgroup.com/license-wizard/de" />
      </Helmet>
      <div className="container content-section">
        <div className="prosa-container">
          <div className="mx-auto shadow p-4 rounded border-light">
            <div className="text-center my-5 px-5">
              <h1><Trans>PTV License Support</Trans></h1>
              <p className="text-muted"><Trans>This short assistant will guide you to the instructions for your license operation.<br />
                Please answer the following three questions to help us understand your use case.</Trans></p>
            </div>
            <ul className="stepper my-4">
              <li className={`step ${(step === 2 || step === 3 || step === 4) ? 'step--complete' : ''} ${(step === 1) ? 'step--active' : ''} `}>
                {(step === 2 || step === 3 || step === 4) ?
                  <span className="step__item" onClick={() => setStep(1)}><i className="fa-solid fa-check"></i></span> :
                  <span className="step__item">1</span>
                }
              </li>
              <li className={`step ${(step === 3 || step === 4) ? 'step--complete' : ''} ${(step === 2) ? 'step--active' : ''} `}>
                {(step === 3 || step === 4) ?
                  <span className="step__item" onClick={() => setStep(2)}><i className="fa-solid fa-check"></i></span> :
                  <span className="step__item">2</span>
                }
              </li>
              <li className={`step ${(step === 3 && stepCompleted) ? 'step--complete' : ''} ${(step === 3) ? 'step--active' : ''} `}>
                {(step === 3 && stepCompleted) ?
                  <span className="step__item" onClick={() => setStep(3)}><i className="fa-solid fa-check"></i></span> :
                  <span className="step__item">3</span>
                }
              </li>
            </ul>
            <div className="p-3">
              <div style={{ 'height': '15rem' }}>
                {conditionalComponent()}
              </div>

              <div className="mt-6">
                {step > 1 &&
                  <div className="float-start">
                    <button className="btn btn-light" onClick={() => setStep(step - 1)}><Trans>Previous</Trans></button>
                  </div>
                }
                {step < 3 &&
                  <div className="float-end">
                    <button className="btn btn-primary" onClick={() => setStep(step + 1)} disabled={!stepCompleted}><Trans>Next</Trans></button>
                  </div>
                }
                {step === 3 &&
                  <>
                    <div className="float-end ">
                      {formData.questionTwo !== '2.3' &&
                        <button className="btn btn-primary" style={{ width: '170px' }} onClick={() => showInstruction()} disabled={!stepCompleted}>
                          {resultLoading ?
                            <i className="loader-btn  text-white"></i>
                            :
                            <Trans>Show instructions</Trans>
                          }
                        </button>
                      }
                    </div>
                  </>
                }
                <div className="clearfix"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Wizard;